import {Button, ButtonGroup} from 'platform/components';
import {Box, Show, Text, useElementSize, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {SyntheticEvent, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {CompositionPhotoCrop} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {thumbnailUrl} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useLayerDrag} from '../hooks/useLayerDrag';
import {LayerBox} from '../types/CompositionEditorAction';
import {RESIZE_HANDLES} from '../types/ResizeHandle';
import {clampCrop} from '../utils/clampCrop';
import {toCssPx} from '../utils/toCssPx';
import {LayerResizeHandle} from './LayerResizeHandle';

interface PhotoCropDialogProps extends TestIdProps {
  crop: CompositionPhotoCrop | undefined;
  onConfirm: (crop: CompositionPhotoCrop) => void;
  onCancel: () => void;
}

type PhotoSize = {width: number; height: number};

const FULL_PHOTO: CompositionPhotoCrop = {x: 0, y: 0, width: 1, height: 1};

// The crop box is edited in photo px, so the editor's layer gestures (move, eight resize handles,
// clamping to the bounds) work on it unchanged; the photo stands in for the canvas.
const toPhotoBox = (crop: CompositionPhotoCrop, photo: PhotoSize): LayerBox => ({
  x: crop.x * photo.width,
  y: crop.y * photo.height,
  width: crop.width * photo.width,
  height: crop.height * photo.height,
});

const toCrop = (box: LayerBox, photo: PhotoSize) =>
  clampCrop({
    x: box.x / photo.width,
    y: box.y / photo.height,
    width: box.width / photo.width,
    height: box.height / photo.height,
  });

const CropSurface = styled.div`
  position: relative;
  overflow: hidden;
  user-select: none;
`;

const CropPhoto = styled.img`
  display: block;
  width: 100%;
  pointer-events: none;
`;

// The frame is computed from the crop box, so its geometry is an inline style. Its wide shadow
// dims the part of the photo outside the crop.
const CropFrame = styled.div.attrs<{$box: LayerBox; $scale: number}>((props) => ({
  style: {
    left: toCssPx(props.$box.x * props.$scale),
    top: toCssPx(props.$box.y * props.$scale),
    width: toCssPx(props.$box.width * props.$scale),
    height: toCssPx(props.$box.height * props.$scale),
  },
}))<{$box: LayerBox; $scale: number}>`
  position: absolute;
  box-sizing: border-box;
  cursor: move;
  touch-action: none;
  outline: 1px solid ${(props) => props.theme.colors.general.accent};
  box-shadow: 0 0 0 100vmax ${(props) => props.theme.colors.palettes.neutral[900][40]};
`;

interface CropAreaProps extends TestIdProps {
  box: LayerBox;
  photo: PhotoSize;
  scale: number;
  onChange: (box: LayerBox) => void;
}

function CropArea(props: CropAreaProps) {
  const drag = useLayerDrag({
    layer: props.box,
    canvas: props.photo,
    scale: props.scale,
    onSelect: () => undefined,
    onCommit: props.onChange,
  });

  return (
    <CropFrame
      {...drag.bindMove()}
      $box={drag.box}
      $scale={props.scale}
      data-testid={suffixTestId('frame', props)}
    >
      {RESIZE_HANDLES.map((handle) => (
        <LayerResizeHandle
          key={handle}
          {...drag.bindResize()}
          $handle={handle}
          data-handle={handle}
          data-testid={suffixTestId(`handle-${handle}`, props)}
        />
      ))}
    </CropFrame>
  );
}

/**
 * Chooses the part of the vehicle photo that the photo slot shows, on the editor's reference
 * photo. The crop is normalised to the photo size, so it applies to every vehicle photo.
 */
export function PhotoCropDialog(props: PhotoCropDialogProps) {
  const [photo, setPhoto] = useState<PhotoSize | null>(null);
  const [editedBox, setEditedBox] = useState<LayerBox | null>(null);
  const [surfaceRef, surfaceWidth] = useElementSize<HTMLDivElement>();

  const box = isNil(photo) ? null : (editedBox ?? toPhotoBox(props.crop ?? FULL_PHOTO, photo));
  const area =
    isNil(photo) || isNil(box) || isNil(surfaceWidth)
      ? null
      : {photo, box, scale: surfaceWidth / photo.width};

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) =>
    setPhoto({width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight});

  return (
    <VStack spacing={4}>
      <Text size="small" color="secondary">
        {i18n.t('entity.promoPhotos.composition.labels.cropPhotoDescription')}
      </Text>
      <Box ref={surfaceRef}>
        <CropSurface>
          <CropPhoto src={`/${thumbnailUrl}`} alt="" draggable={false} onLoad={handleLoad} />
          <Show when={area}>
            {(area) => (
              <CropArea
                box={area.box}
                photo={area.photo}
                scale={area.scale}
                onChange={setEditedBox}
                data-testid={suffixTestId('area', props)}
              />
            )}
          </Show>
        </CropSurface>
      </Box>
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.cancel')}
          onClick={props.onCancel}
          data-testid={suffixTestId('cancel', props)}
        />
        <Button
          variant="primary"
          title={i18n.t('general.actions.confirm')}
          isDisabled={isNil(area)}
          onClick={() => {
            if (isNotNil(area)) {
              props.onConfirm(toCrop(area.box, area.photo));
            }
          }}
          data-testid={suffixTestId('confirm', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
